import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Plus,
  Edit,
  User,
  MapPin,
  Phone,
  Target,
  DollarSign,
  Calendar,
  Tag,
  Building,
  Mail,
  Globe
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useLocation } from 'react-router-dom'
import prospectMasterService from '../../services/prospectMasterService'
import { formatRupeeAmount, formatINR } from '../../utils/currencyFormat';

const ProspectMasterView = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const prospectData = location.state?.prospectData || {}

  const [activeTab, setActiveTab] = useState('prospectInfo')
  const [prospect, setProspect] = useState(null)
  const [loading, setLoading] = useState(true)

  // Load prospect data on component mount
  useEffect(() => {
    if (prospectData._id) {
      loadProspectData()
    } else {
      navigate('/crm/prospect-master')
    }
  }, [])

  const loadProspectData = async () => {
    try {
      setLoading(true)
      const response = await prospectMasterService.getProspectById(prospectData._id)
      if (response.success) {
        setProspect(response.data)
      } else {
        navigate('/crm/prospect-master')
      }
    } catch (error) {
      console.error('Error loading prospect data:', error)
      navigate('/crm/prospect-master')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    navigate('/crm/prospect-master/edit', { 
      state: { prospectData: prospect } 
    })
  }

  const handleBack = () => {
    navigate('/crm/prospect-master')
  }

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Masters',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Prospect Master',
      path: '/crm/prospect-master',
      onClick: () => navigate('/crm/prospect-master')
    },
    {
      label: 'View Prospect',
      path: '/crm/prospect-master/view',
      isCurrent: true
    }
  ]

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading prospect details...</p>
        </div>
      </div>
    )
  }

  if (!prospect) {
    return (
      <div className="sales-dashboard">
        <div className="error-container">
          <h2>Prospect not found</h2>
          <p>The requested prospect could not be found.</p>
          <button onClick={handleBack} className="btn-primary">
            <ArrowLeft size={16} />
            Back to Prospects
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Prospect Details</h1>
          <p>View prospect information and details</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-secondary"
            onClick={handleBack}
          >
            <ArrowLeft size={16} />
            Back to Prospects
          </button>
          <button 
            className="btn-primary"
            onClick={handleEdit}
          >
            <Edit size={16} />
            Edit Prospect
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="form-tabs">
        <button
          className={`tab-button ${activeTab === 'prospectInfo' ? 'active' : ''}`}
          onClick={() => setActiveTab('prospectInfo')}
        >
          <User size={16} />
          <span>Prospect Info</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'addresses' ? 'active' : ''}`}
          onClick={() => setActiveTab('addresses')}
        >
          <MapPin size={16} />
          <span>Addresses</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          <Phone size={16} />
          <span>Contacts</span>
        </button>
        <button
          className={`tab-button ${activeTab === 'bankDetails' ? 'active' : ''}`}
          onClick={() => setActiveTab('bankDetails')}
        >
          <DollarSign size={16} />
          <span>Bank Details</span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'prospectInfo' && (
        <div className="tab-content">
          <div className="view-section">
            <h3>Basic Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Prospect Code</label>
                <div className="info-value">
                  <Building size={16} className="text-gray-400" />
                  <span>{prospect.prospectCode}</span>
                </div>
              </div>
              <div className="info-item">
                <label>Prospect Name</label>
                <div className="info-value">
                  <User size={16} className="text-gray-400" />
                  <span>{prospect.prospectName}</span>
                </div>
              </div>
              <div className="info-item">
                <label>Nick Name</label>
                <div className="info-value">
                  <span>{prospect.prospectNickName || 'N/A'}</span>
                </div>
              </div>
              <div className="info-item">
                <label>Category</label>
                <div className="info-value">
                  <span className="badge badge-blue">{prospect.prospectCategory}</span>
                </div>
              </div>
              <div className="info-item">
                <label>Type</label>
                <div className="info-value">
                  <span>{prospect.prospectType || 'N/A'}</span>
                </div>
              </div>
              <div className="info-item">
                <label>Region</label>
                <div className="info-value">
                  <MapPin size={16} className="text-gray-400" />
                  <span>{prospect.region}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="view-section">
            <h3>Lead Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Lead Source</label>
                <div className="info-value">
                  <span>{prospect.leadSource || 'N/A'}</span>
                </div>
              </div>
              <div className="info-item">
                <label>Lead Score</label>
                <div className="info-value">
                  <div className="score-container">
                    <div className="score-bar">
                      <div 
                        className="score-fill" 
                        style={{ width: `${prospect.leadScore}%` }}
                      ></div>
                    </div>
                    <span className="score-text">{prospect.leadScore}%</span>
                  </div>
                </div>
              </div>
              <div className="info-item">
                <label>Prospect Stage</label>
                <div className="info-value">
                  <span className={`badge ${
                    prospect.prospectStage === 'Closed Won' ? 'badge-green' :
                    prospect.prospectStage === 'Closed Lost' ? 'badge-red' :
                    prospect.prospectStage === 'Negotiation' ? 'badge-orange' :
                    'badge-blue'
                  }`}>
                    {prospect.prospectStage}
                  </span>
                </div>
              </div>
              <div className="info-item">
                <label>Status</label>
                <div className="info-value">
                  <span className={`badge ${
                    prospect.prospectStatus === 'Active' ? 'badge-green' :
                    prospect.prospectStatus === 'Converted' ? 'badge-blue' :
                    'badge-gray'
                  }`}>
                    {prospect.prospectStatus}
                  </span>
                </div>
              </div>
              <div className="info-item">
                <label>Expected Deal Value</label>
                <div className="info-value">
                  <DollarSign size={16} className="text-gray-400" />
                  <span>{prospect.expectedDealValue ? formatRupeeAmount(prospect.expectedDealValue) : 'N/A'}</span>
                </div>
              </div>
              <div className="info-item">
                <label>Expected Close Date</label>
                <div className="info-value">
                  <Calendar size={16} className="text-gray-400" />
                  <span>{prospect.expectedCloseDate ? new Date(prospect.expectedCloseDate).toLocaleDateString() : 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="view-section">
            <h3>Additional Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Website</label>
                <div className="info-value">
                  {prospect.prospectWebsite ? (
                    <a 
                      href={prospect.prospectWebsite} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="link-with-icon"
                    >
                      <Globe size={16} className="text-gray-400" />
                      <span>{prospect.prospectWebsite}</span>
                    </a>
                  ) : (
                    <span>N/A</span>
                  )}
                </div>
              </div>
              <div className="info-item">
                <label>PAN Number</label>
                <div className="info-value">
                  <span>{prospect.prospectPAN || 'N/A'}</span>
                </div>
              </div>
              <div className="info-item">
                <label>GSTIN</label>
                <div className="info-value">
                  <span>{prospect.GSTIN || 'N/A'}</span>
                </div>
              </div>
              <div className="info-item">
                <label>GST Classification</label>
                <div className="info-value">
                  <span>{prospect.GSTClassification || 'N/A'}</span>
                </div>
              </div>
              <div className="info-item full-width">
                <label>Notes</label>
                <div className="info-value">
                  <span>{prospect.notes || 'No notes available'}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'addresses' && (
        <div className="tab-content">
          <div className="view-section">
            <h3>Address Information</h3>
            {prospect.prospectBillingAddress && prospect.prospectBillingAddress.length > 0 ? (
              <div className="address-list">
                {prospect.prospectBillingAddress.map((address, index) => (
                  <div key={index} className="address-card">
                    <h4>Address {index + 1}</h4>
                    <div className="address-details">
                      {address.line1 && <p><strong>Line 1:</strong> {address.line1}</p>}
                      {address.line2 && <p><strong>Line 2:</strong> {address.line2}</p>}
                      {address.line3 && <p><strong>Line 3:</strong> {address.line3}</p>}
                      {address.line4 && <p><strong>Line 4:</strong> {address.line4}</p>}
                      {address.city && <p><strong>City:</strong> {address.city}</p>}
                      {address.state && <p><strong>State:</strong> {address.state}</p>}
                      {address.pinCode && <p><strong>Pin Code:</strong> {address.pinCode}</p>}
                      {address.country && <p><strong>Country:</strong> {address.country}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <MapPin size={48} className="text-gray-300" />
                <p>No addresses found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'contacts' && (
        <div className="tab-content">
          <div className="view-section">
            <h3>Contact Information</h3>
            {prospect.prospectContactInfo && prospect.prospectContactInfo.length > 0 ? (
              <div className="contact-list">
                {prospect.prospectContactInfo.map((contact, index) => (
                  <div key={index} className="contact-card">
                    <h4>Contact {index + 1}</h4>
                    <div className="contact-details">
                      {contact.contactPersonName && (
                        <div className="contact-item">
                          <User size={16} className="text-gray-400" />
                          <span>{contact.contactPersonName}</span>
                        </div>
                      )}
                      {contact.contactPersonEmail && (
                        <div className="contact-item">
                          <Mail size={16} className="text-gray-400" />
                          <span>{contact.contactPersonEmail}</span>
                        </div>
                      )}
                      {contact.contactPersonNumber && (
                        <div className="contact-item">
                          <Phone size={16} className="text-gray-400" />
                          <span>{contact.contactPersonNumber}</span>
                        </div>
                      )}
                      {contact.contactPersonDesignation && (
                        <p><strong>Designation:</strong> {contact.contactPersonDesignation}</p>
                      )}
                      {contact.contactPersonDepartment && (
                        <p><strong>Department:</strong> {contact.contactPersonDepartment}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <Phone size={48} className="text-gray-300" />
                <p>No contacts found</p>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'bankDetails' && (
        <div className="tab-content">
          <div className="view-section">
            <h3>Bank Details</h3>
            {prospect.prospectBankDetails && prospect.prospectBankDetails.length > 0 ? (
              <div className="bank-list">
                {prospect.prospectBankDetails.map((bank, index) => (
                  <div key={index} className="bank-card">
                    <h4>Bank Detail {index + 1}</h4>
                    <div className="bank-details">
                      {bank.bankName && <p><strong>Bank Name:</strong> {bank.bankName}</p>}
                      {bank.accountNumber && <p><strong>Account Number:</strong> {bank.accountNumber}</p>}
                      {bank.ifscCode && <p><strong>IFSC Code:</strong> {bank.ifscCode}</p>}
                      {bank.branchName && <p><strong>Branch Name:</strong> {bank.branchName}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <DollarSign size={48} className="text-gray-300" />
                <p>No bank details found</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default ProspectMasterView
